import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Trophy, Coins, ChevronRight, BookOpen, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface AssessmentCompleteProps {
  type: string;
  coinsEarned: number; // Can be negative for paid assessments
  learningPath?: CareerPathNode | null;
  achievements?: Achievement[];
  levelUp?: CareerLevel | null;
  onContinue: () => void;
}

export function AssessmentComplete({ 
  type, 
  coinsEarned, 
  learningPath,
  onContinue 
}: AssessmentCompleteProps) {
  const navigate = useNavigate();
  const [showCoins, setShowCoins] = useState(false);

  useEffect(() => {
    console.log('AssessmentComplete mounted');
    // Trigger confetti animation
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    // Show coins animation after a delay
    const timer = setTimeout(() => setShowCoins(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-lg p-8 max-w-md w-full mx-4"
      >
        <div className="text-center space-y-6">
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
          >
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <Trophy className="w-8 h-8 text-primary-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Assessment Complete!
            </h2>
            <p className="text-gray-600">
              {type === 'initial' 
                ? 'Great job completing your initial assessment!' 
                : 'Excellent work on completing your skills assessment!'}
            </p>
          </motion.div>

          {showCoins && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="py-4"
            >
              <div className="text-2xl font-bold text-yellow-500">
                +{coinsEarned} Coins
              </div>
            </motion.div>
          )}

          {learningPath && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-primary-50 p-4 rounded-lg"
            >
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-primary-600" />
                <span className="font-medium">Your Learning Path is Ready!</span>
              </div>
              <p className="text-sm text-gray-600">
                We've created a personalized learning path based on your assessment.
              </p>
            </motion.div>
          )}

          <div className="flex flex-col gap-3 pt-4">
            {learningPath && (
              <Button
                onClick={() => navigate('/learning-path')}
                variant="default"
                className="w-full"
              >
                View Learning Path
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
} 